import React, { useState } from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent, pad2 } from './Shared';
import { LocalCurrencyNote } from './LocalCurrencyNote';
import { useCountdown } from '../hooks/useCountdown';
import { markCheckoutStarted } from '../hooks/useExitIntent';
import { PRODUCTS } from './MetaPixel';
import { volumes, sessionFeatures, featureChips } from '../data/volumes';
import { bonuses } from '../data/bonuses';
import { Check, AlertTriangle, Plus, PrinterIcon, ArrowRightIcon, Lock, ZapIcon, ShieldCheck, BookOpenIcon, GiftIcon } from 'lucide-react';
export const ProductTomos = () => {
  const [openVolume, setOpenVolume] = useState<number | null>(0);
  return <Section className="bg-slate-950" data-id="element-28" data-mp="2cpyljmxwcc4x">
      <FadeIn data-id="element-29" data-mp="2cowzyoj9878y">
        <SectionTitle sub="One volume every two months. Open it, pick the session, teach it." data-id="element-30" data-mp="2cof6u0rw2959">
          6 complete <Accent data-id="element-31" data-mp="30e9g68d01fkr">volumes</Accent>
        </SectionTitle>
      </FadeIn>

      {/* Year timeline */}
      <FadeIn delay={0.05} className="mx-auto mb-8 max-w-4xl" data-id="element-32" data-mp="357kdnivx8k00">
        <div className="grid grid-cols-6 gap-1.5 md:gap-2" aria-label="Your year, volume by volume" data-id="element-33" data-mp="2hktkotud4qod">
          {volumes.map(v => <div key={v.number} data-id="element-34" data-mp="2cntemixijnx9">
              <div className={`h-1.5 rounded-full ${v.bar}`} data-id="element-35" data-mp="2cknkgpa11vuo" />
              <p className={`mt-2 font-display text-[11px] font-extrabold uppercase md:text-sm ${v.text}`} data-id="element-36" data-mp="2rc69tdos12qp">
                Vol. {v.number}
              </p>
              <p className="hidden text-xs text-slate-500 md:block" data-id="element-37" data-mp="30bt1h7rpeu6k">{v.months}</p>
            </div>)}
        </div>
      </FadeIn>

      <FadeIn delay={0.1} className="mb-12 md:mb-16" data-id="element-38" data-mp="2w9fxanslv4bw">
        <ul className="mx-auto flex max-w-3xl flex-wrap justify-center gap-1.5 md:gap-2" data-id="element-39" data-mp="3aa8szbun85gl">
          {featureChips.map(chip => <li key={chip} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 md:text-sm" data-id="element-40" data-mp="35bfh1nv08mij">
              <Check size={12} className="text-sky-400" strokeWidth={3} aria-hidden="true" data-id="element-41" data-mp="1axejd6b6y5k1" />
              {chip}
            </li>)}
        </ul>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400 md:text-sm" data-id="element-42" data-mp="3b0yusn43n3qx">
          <PrinterIcon size={14} aria-hidden="true" data-id="element-43" data-mp="3fyquw4ewdw67" />
          <span data-id="element-44" data-mp="3jzsxmv80wqcx"><span className="font-semibold text-slate-200" data-id="element-45" data-mp="3jz12flic8un6">Print-ready</span> or use it straight from your phone or tablet.</span>
        </p>
      </FadeIn>

      <h3 className="mb-5 text-center text-xs font-semibold uppercase tracking-widest text-slate-400" data-id="element-46" data-mp="3owsv4hqt4ksf">
        What you get
      </h3>
      <div className="mb-16 grid grid-cols-1 items-start gap-3 md:mb-24 md:grid-cols-2 md:gap-4 lg:grid-cols-3" data-id="element-47" data-mp="2r5o7g5bogzj">
        {volumes.map((v, i) => {
        const isOpen = openVolume === i;
        return <FadeIn key={v.number} delay={Math.min(i * 0.04, 0.2)} data-id="element-48" data-mp="bksfasyl4w2e" data-mpi={i}>
              <button type="button" onClick={() => setOpenVolume(isOpen ? null : i)} className={`group w-full rounded-2xl border border-t-[3px] bg-white/[0.03] p-5 text-left transition-[border-color,background-color] duration-200 ease-out hover:bg-white/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 md:p-6 ${v.border} ${isOpen ? 'border-x-white/20 border-b-white/20' : 'border-x-white/10 border-b-white/10'}`} aria-expanded={isOpen} data-id="element-49" data-mp="bm3jgtvy05u1">
                <div className="flex items-start justify-between gap-3" data-id="element-50" data-mp="hn41inlm9gjk">
                  <div className="min-w-0" data-id="element-51" data-mp="1ecv9npmtz27u">
                    <p className={`font-display text-xs font-extrabold uppercase tracking-wide ${v.text}`} data-id="element-52" data-mp="1ebtm4lomon98">
                      Volume {v.number} · {v.months}
                    </p>
                    <h4 className="mt-1.5 font-display text-lg font-extrabold leading-tight text-white md:text-xl" data-id="element-53" data-mp="14ma39g0x0ted">
                      {v.title}
                    </h4>
                    <p className="mt-1 text-sm text-slate-400" data-id="element-54" data-mp="1x3cnyp6x1v9o">{v.tagline}</p>
                  </div>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-white transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-55" data-mp="1scnu537rkm7d">
                    <Plus size={16} data-id="element-56" data-mp="1hu909skneop1" />
                  </span>
                </div>
                <div className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-57" data-mp="13u7xg7ktp76x">
                  <div className="overflow-hidden" data-id="element-58" data-mp="17xmg7605c1o2">
                    <p className="pt-4 text-[15px] leading-relaxed text-slate-300" data-id="element-59" data-mp="1vm5mxh3js6e1">
                      {v.desc}
                    </p>
                    <p className="mt-3 inline-flex rounded-md bg-white/5 px-2 py-0.5 text-xs font-semibold text-slate-200" data-id="element-60" data-mp="20mbih8cxud1e">
                      {v.sessions}
                    </p>
                  </div>
                </div>
              </button>
            </FadeIn>;
      })}
      </div>

      <FadeIn data-id="element-61" data-mp="2eip0qjpaqigx">
        <div className="grid gap-8 rounded-3xl border border-white/10 bg-slate-900/60 p-6 md:grid-cols-[0.9fr_1.1fr] md:gap-12 md:p-12" data-id="element-62" data-mp="2ehvr9ayydev3">
          <div className="flex flex-col" data-id="element-63" data-mp="7v7szap6wrur">
            <h3 className="font-display text-3xl font-black uppercase italic leading-[0.95] tracking-tight text-white md:text-5xl" data-id="element-64" data-mp="7twrxin4py7c">
              Every session <Accent data-id="element-65" data-mp="hl9h22hjma9o">includes</Accent>
            </h3>
            <p className="mt-5 text-lg font-semibold leading-snug text-sky-300 md:mt-auto md:text-xl" data-id="element-66" data-mp="hkrdeksjwtrq">
              Open the volume, pick the session and teach. That simple. 365
              high-performance classes. Ready today.
            </p>
          </div>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-67" data-mp="3g3y92auk3e6r">
            {sessionFeatures.map(feature => <li key={feature.title} className="flex items-start gap-3" data-id="element-68" data-mp="3v1gvv7nobhbk">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15" data-id="element-69" data-mp="3q0tap2m4lup9">
                  <Check size={12} className="text-emerald-400" strokeWidth={3} aria-hidden="true" data-id="element-70" data-mp="1a95b0zfq9o2r" />
                </span>
                <div data-id="element-71" data-mp="15au52zsjzxpt">
                  <p className="font-semibold leading-snug text-white" data-id="element-72" data-mp="15dg8qocbpt2r">{feature.title}</p>
                  <p className="text-sm leading-normal text-slate-400" data-id="element-73" data-mp="1cgeftpn0dc21">{feature.desc}</p>
                </div>
              </li>)}
          </ul>
        </div>
      </FadeIn>
    </Section>;
};
export const Bonuses = () => {
  return <Section className="bg-slate-900/40" data-id="element-74" data-mp="1rbaw8q4z15q7">
      <FadeIn data-id="element-75" data-mp="1ra9lynvs4n9j">
        <SectionTitle sub="Exclusive materials — at no extra cost" data-id="element-76" data-mp="1rat2c05s73lg">
          + 5 strategic <Accent data-id="element-77" data-mp="tp53l8lus96r">bonuses</Accent>
        </SectionTitle>
      </FadeIn>

      <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mb-10 md:gap-4 lg:grid-cols-5" data-id="element-78" data-mp="ymdmxciqmj9l">
        {bonuses.map((bono, i) => <FadeIn key={bono.num} delay={Math.min(i * 0.04, 0.2)} className={i === bonuses.length - 1 ? 'sm:col-span-2 lg:col-span-1' : ''} data-id="element-79" data-mp="13l88unc04nxx" data-mpi={i}>
            <article className="flex h-full gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-3 sm:flex-col sm:gap-0 sm:p-0" data-id="element-80" data-mp="18ejdchtccsmo">
              <img src={bono.image} alt={`Bonus ${bono.num} cover: ${bono.title}`} loading="lazy" decoding="async" width={1024} height={1024} className="aspect-square w-28 shrink-0 self-start rounded-xl object-cover sm:w-full sm:rounded-none" data-id="element-81" data-mp="fv4g5b0cq8lp" />
              <div className="flex min-w-0 flex-1 flex-col sm:p-4" data-id="element-82" data-mp="2p398xtgkob3u">
                <p className="font-display text-xs font-extrabold uppercase tracking-wide text-sky-400" data-id="element-83" data-mp="2k4fsbs5zo3zg">
                  Bonus {bono.num}
                </p>
                <h3 className="mt-1 font-display text-base font-extrabold leading-tight text-white" data-id="element-84" data-mp="2a8co8grktv9v">
                  {bono.title}
                </h3>
                <p className="mt-1.5 text-sm leading-snug text-slate-400" data-id="element-85" data-mp="25f02qfi724lc">
                  {bono.desc}
                </p>
                <div className="mt-auto flex items-center justify-between gap-2 pt-3" data-id="element-86" data-mp="1wgelqosdexmo">
                  <span className="text-sm text-slate-500 line-through" data-id="element-87" data-mp="1welgndxeolgs">
                    {bono.value}
                  </span>
                  <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-400" data-id="element-88" data-mp="1mhqmf786178b">
                    Free
                  </span>
                </div>
              </div>
            </article>
          </FadeIn>)}
      </div>

      <FadeIn className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-6 py-5 text-center md:flex-row md:text-left" data-id="element-89" data-mp="31eicvrpo0ubr">
        <div className="flex items-center gap-3" data-id="element-90" data-mp="2roozcl5yq54v">
          <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 md:flex" data-id="element-91" data-mp="2rn5lf17s4669">
            <GiftIcon size={22} className="text-emerald-400" aria-hidden="true" data-id="element-92" data-mp="2wh02rxzww926" />
          </span>
          <div data-id="element-93" data-mp="2ilxn4j59j5c6">
            <p className="text-sm text-slate-300" data-id="element-94" data-mp="2nmv6v05x87fs">Total value of the 5 bonuses: <span className="text-slate-500 line-through" data-id="element-95" data-mp="2nn3helekn0nt">$50</span></p>
            <p className="text-xs text-slate-500" data-id="element-96" data-mp="28qvyrpita7an">Included with Functional Fitness 365, at no extra cost.</p>
          </div>
        </div>
        <p className="font-display text-3xl font-black uppercase italic leading-none tracking-tight text-emerald-400 md:text-4xl" data-id="element-97" data-mp="2dqs6nzumqivh">
          Free today
        </p>
      </FadeIn>
    </Section>;
};
export const UrgencyCounter = () => {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const units = [{
    value: hours,
    label: 'hrs'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'sec'
  }];
  return <Section className="bg-slate-950 !py-14 md:!py-20" data-id="element-98" data-mp="2d0oqm3zhxrzz">
      <FadeIn className="mx-auto max-w-xl text-center" data-id="element-99" data-mp="2904th0exaodu">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-400" data-id="element-100" data-mp="28zmzybjnao9m">
          Launch price — ends in
        </p>
        <div className="mt-4 flex items-start justify-center gap-2 md:gap-3" role="timer" aria-live="off" data-id="element-101" data-mp="2mtdyypzr2pr3">
          {units.map((unit, i) => <React.Fragment key={unit.label} data-id="element-102" data-mp="2wp7ln276nzml" data-mpi={i}>
              {i > 0 && <span className="pt-3 font-display text-4xl font-black text-slate-600 md:pt-4 md:text-5xl" aria-hidden="true" data-id="element-103" data-mp="2wnmpx994uan5">:</span>}
              <div className="flex w-20 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] py-3 md:w-24 md:py-4" data-id="element-104" data-mp="2qx91cwyal3ci">
                <span className="font-display text-4xl font-black tabular-nums leading-none tracking-tight text-white md:text-5xl" data-id="element-105" data-mp="2m60qrxl87bxr">
                  {pad2(unit.value)}
                </span>
                <span className="mt-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500" data-id="element-106" data-mp="2d75imfymfrrt">
                  {unit.label}
                </span>
              </div>
            </React.Fragment>)}
        </div>

        <p className="mt-6 flex items-center justify-center gap-2 text-base font-semibold text-amber-300 md:text-lg" data-id="element-107" data-mp="34y39dehfh2an">
          <AlertTriangle size={18} aria-hidden="true" data-id="element-108" data-mp="30xjd0d4oi2pk" />
          After that, the price goes up. For good.
        </p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left" data-id="element-109" data-mp="1pg9w9ycxpory">
          <div className="mb-3 flex justify-between gap-3 text-sm text-slate-300" data-id="element-110" data-mp="1khq7zravc0wf">
            <span className="flex items-center gap-2" data-id="element-111" data-mp="1kjt66ym6d0c8">
              <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" aria-hidden="true" data-id="element-112" data-mp="pebzi5uapr" />
              Spots at launch price
            </span>
            <span className="font-semibold text-sky-400" data-id="element-113" data-mp="90457028onas">Few left</span>
          </div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={71} aria-valuemin={0} aria-valuemax={100} aria-label="Spots already taken" data-id="element-114" data-mp="8y9gdanukb1f">
            <div className="h-full rounded-full bg-blue-500" style={{
            width: '71%'
          }} data-id="element-115" data-mp="is9uu350qsmt" />
          </div>
          <p className="text-right text-xs text-slate-500" data-id="element-116" data-mp="socxrb7p2vat">
            71% of spots already taken
          </p>
        </div>
      </FadeIn>
    </Section>;
};
export const OfferCard = ({
  id,
  footnote
}: {
  id?: string;
  footnote: string;
}) => {
  const includes = [{
    icon: BookOpenIcon,
    label: '6 Complete Volumes',
    detail: '365 sessions ready to teach',
    price: '$69'
  }, {
    icon: GiftIcon,
    label: '5 Exclusive Bonuses',
    detail: 'Adaptations, warm-ups, closers & scripts',
    price: '$50'
  }];
  return <div id={id} className="relative rounded-3xl border border-sky-400/40 bg-slate-900 p-5 shadow-2xl shadow-blue-950/50 md:p-8" data-id="element-117" data-mp="orxwe6kank7">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white" data-id="element-118" data-mp="el55mvlanalh">
        Most popular
      </div>

      <p className="mt-2 text-center font-display text-xl font-black uppercase italic tracking-tight text-white md:text-2xl" data-id="element-119" data-mp="oj2unlaz9bf5">
        Functional Fitness <Accent data-id="element-120" data-mp="ya76ic5cwg5r">365™</Accent>
      </p>

      <ul className="mt-5 divide-y divide-white/10 border-y border-white/10" data-id="element-121" data-mp="ydbfdmtro02r">
        {includes.map(({
        icon: Icon,
        label,
        detail,
        price
      }) => <li key={label} className="flex items-center justify-between gap-3 py-3" data-id="element-122" data-mp="17bdoyqrkg11n">
            <div className="flex min-w-0 items-start gap-3" data-id="element-123" data-mp="12ctz4f9uq540">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5" data-id="element-124" data-mp="12enag7vetdic">
                <Icon size={16} className="text-sky-400" aria-hidden="true" data-id="element-125" data-mp="1lzf7ournqzwt" />
              </span>
              <div className="min-w-0" data-id="element-126" data-mp="1qwxeaei7d31n">
                <p className="text-sm font-semibold leading-snug text-white md:text-base" data-id="element-127" data-mp="1cy7fmufqivb6">{label}</p>
                <p className="text-xs leading-snug text-slate-400 md:text-sm" data-id="element-128" data-mp="1gyzsryrq9kcq">{detail}</p>
              </div>
            </div>
            <span className="shrink-0 text-sm text-slate-500 line-through" data-id="element-129" data-mp="13731fwxvvehe">{price}</span>
          </li>)}
      </ul>

      <div className="mt-6 text-center" data-id="element-130" data-mp="180fgbfcrg6wy">
        <p className="text-sm text-slate-400" data-id="element-131" data-mp="t9yi04vf9x4a">
          Original value <span className="text-slate-500 line-through" data-id="element-132" data-mp="tbro9j24ifbr">$119</span> · Full access today for
        </p>
        <p className="mt-2 font-display font-black italic leading-none tracking-tight text-white" data-id="element-133" data-mp="y9b2iiytms3p">
          <span className="align-top text-3xl md:text-4xl" data-id="element-134" data-mp="ka1w2b0f3ccx">$</span>
          <span className="text-7xl md:text-8xl" data-id="element-135" data-mp="p7bidhsif7wj">19</span>
          <span className="ml-1 text-base font-bold not-italic text-slate-400" data-id="element-136" data-mp="h7nogjxs392y">USD</span>
        </p>
        <p className="mt-2 text-xs text-slate-400 md:text-sm" data-id="element-137" data-mp="c8khgty8mssc">{footnote}</p>
      </div>

      <div className="mt-6" data-id="element-138" data-mp="cawx30giwr28">
        <CTAButton href={PRODUCTS.ff365.checkoutUrl} fullWidth trackingProduct="ff365" onClick={markCheckoutStarted} data-id="element-139" data-mp="7hc7nwszx5en">
          Get the complete pack
          <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-140" data-mp="3g0gvnydye4v" />
        </CTAButton>
        <LocalCurrencyNote className="mt-4" data-id="element-141" data-mp="3pmxjx975xg1r" />
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500" data-id="element-142" data-mp="3pm5qo511fuen">
          <span className="flex items-center gap-1" data-id="element-143" data-mp="3kq784y2e56m5"><Lock size={12} aria-hidden="true" data-id="element-144" data-mp="3gnkbmjuryxim" /> Secure checkout</span>
          <span className="flex items-center gap-1" data-id="element-145" data-mp="1ix0u8n2c82ex"><ZapIcon size={12} aria-hidden="true" data-id="element-146" data-mp="1ivzkqn0yznzb" /> Access in minutes</span>
          <span className="flex items-center gap-1" data-id="element-147" data-mp="1euw5a1mu3tym"><ShieldCheck size={12} aria-hidden="true" data-id="element-148" data-mp="1evxes1o7c8e8" /> 7-day guarantee</span>
        </div>
      </div>
    </div>;
};
export const Pricing = () => {
  return <Section className="bg-slate-900/40" id="pricing" data-id="element-149" data-mp="31rvnixrpc138">
      <div className="mx-auto max-w-lg" data-id="element-150" data-mp="31qasz85e0i5d">
        <FadeIn className="mb-10 text-center" data-id="element-151" data-mp="36j3yvzcldwj1">
          <p className="text-sm text-slate-400 md:text-base" data-id="element-152" data-mp="36jdmxhe2ttm3">
            You're one step away from having every class solved…
          </p>
          <h2 className="mx-auto mt-3 font-display text-4xl font-black uppercase italic leading-[0.92] tracking-tight text-white md:text-6xl" data-id="element-153" data-mp="2du6gzxcam1qt">
            Special <Accent data-id="element-154" data-mp="2itt3yyhxawct">launch</Accent> offer
          </h2>
          <p className="mt-3 text-sm text-slate-400" data-id="element-155" data-mp="2mv4jucazw37t">
            Volumes, bonuses and 365 ready sessions in one single access.
          </p>
        </FadeIn>

        <FadeIn delay={0.05} data-id="element-156" data-mp="2rv93ucb43g2s">
          <OfferCard footnote="One-time payment · No subscriptions · Instant access" data-id="element-157" data-mp="24njjhaes02k" />
        </FadeIn>
      </div>
    </Section>;
};